import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import "@/App.css";
import { Toaster } from "sonner";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import ROIStrip from "./components/ROIStrip";
import TrustBadges from "./components/TrustBadges";
import TrustBar from "./components/TrustBar";
import Modules from "./components/Modules";
import PainVsCleany from "./components/PainVsCleany";
import Personas from "./components/Personas";
import Comparison from "./components/Comparison";
import MobileShowcase from "./components/MobileShowcase";
import HowItWorks from "./components/HowItWorks";
import BookDemoBlock from "./components/BookDemoBlock";
import Pricing from "./components/Pricing";
import FAQ from "./components/FAQ";
import About from "./components/About";
import ContactSection from "./components/ContactSection";
import Footer from "./components/Footer";
import WaitlistDialog from "./components/WaitlistDialog";
import Countdown from "./components/Countdown";
import CountdownStrip from "./components/CountdownStrip";
import MobileStickyCTA from "./components/MobileStickyCTA";
import ExitIntentPopup from "./components/ExitIntentPopup";
import CookieConsent from "./components/CookieConsent";
import Analytics from "./components/Analytics";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import CookiesPage from "./pages/CookiesPage";
import UpdatesIndexPage from "./pages/UpdatesIndexPage";
import UpdatePostPage from "./pages/UpdatePostPage";
import DpaPage from "./pages/DpaPage";
import BeregnPrisPage from "./pages/BeregnPrisPage";
import ForsideV2Page from "./pages/ForsideV2Page";
import TrialLandingPage from "./pages/TrialLandingPage";
import MesseLandingPage from "./pages/MesseLandingPage";
import DemoPage from "./pages/DemoPage";
import ChatPrivacyPage from "./pages/ChatPrivacyPage";
import ChatVilkaarPage from "./pages/ChatVilkaarPage";
import CrmLayout from "./pages/crm/CrmLayout";
import CrmPipeline from "./pages/crm/CrmPipeline";
import CrmLeads from "./pages/crm/CrmLeads";
import CrmTemplates from "./pages/crm/CrmTemplates";
import CrmFollowups from "./pages/crm/CrmFollowups";
import CrmSettings from "./pages/crm/CrmSettings";
import CrmDigest from "./pages/crm/CrmDigest";
import LatestUpdatesStrip from "./components/LatestUpdatesStrip";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminTablePage from "./pages/admin/AdminTablePage";
import AdminPostsList from "./pages/admin/AdminPostsList";
import AdminPostEditor from "./pages/admin/AdminPostEditor";
import AdminLegalDpaPage from "./pages/admin/AdminLegalDpaPage";
import PageviewTracker from "./components/PageviewTracker";
import ScrollToTop from "./components/ScrollToTop";
import { applyConsent } from "./lib/consent";

const REF_KEY = "cleany_referred_by";

function HomePage() {
    const navigate = useNavigate();
    // Capture ?ref= from URL on first load and persist
    useEffect(() => {
        document.title = "Cleany.dk – Driftsplatform til rengøringsfirmaer";
        const params = new URLSearchParams(window.location.search);
        const ref = params.get("ref");
        if (ref) {
            try {
                localStorage.setItem(REF_KEY, ref);
            } catch (e) {
                console.warn("App: failed to persist referral code", e);
            }
        }
    }, []);

    const openWaitlist = (src = "cta") =>
        window.dispatchEvent(new CustomEvent("cleany:open-waitlist", { detail: { source: src } }));

    return (
        <>
            <CountdownStrip onCtaClick={() => openWaitlist("strip")} />
            <Navbar onCtaClick={() => openWaitlist("navbar")} />
            <main className="pb-20 md:pb-0">
                <Hero onCtaClick={() => openWaitlist("hero")} />
                <ROIStrip onCtaClick={() => navigate("/beregn-pris")} />
                <TrustBadges />
                <TrustBar />
                <PainVsCleany />
                <Personas />
                <Comparison />
                <Modules />
                <MobileShowcase />
                <Countdown onCtaClick={() => openWaitlist("countdown")} />
                <HowItWorks />
                <BookDemoBlock />
                <Pricing onCtaClick={() => openWaitlist("pricing")} />
                <FAQ />
                <LatestUpdatesStrip />
                <About />
                <ContactSection />
            </main>
            <Footer onCtaClick={() => openWaitlist("footer")} />

            <ExitIntentPopup onCtaClick={() => openWaitlist("exit-intent")} />
            <MobileStickyCTA onCtaClick={() => openWaitlist("mobile-sticky")} />
        </>
    );
}

function App() {
    // Allow other parts of the app (e.g. footer link, cookie page button)
    // to re-open the cookie banner.
    const [cookieOpen, setCookieOpen] = useState(false);
    useEffect(() => {
        const handler = () => setCookieOpen(true);
        window.addEventListener("cleany:open-cookie-settings", handler);
        return () => window.removeEventListener("cleany:open-cookie-settings", handler);
    }, []);

    // Global WaitlistDialog — any page can fire `cleany:open-waitlist`.
    const [waitlistOpen, setWaitlistOpen] = useState(false);
    const [waitlistSource, setWaitlistSource] = useState("global");
    useEffect(() => {
        const handler = (e) => {
            setWaitlistSource(e?.detail?.source || "global");
            setWaitlistOpen(true);
        };
        window.addEventListener("cleany:open-waitlist", handler);
        return () => window.removeEventListener("cleany:open-waitlist", handler);
    }, []);

    // On mount: re-apply saved consent so Clarity (and any late-loaded tag)
    // gets the signal for returning visitors who don't see the banner again.
    useEffect(() => {
        try {
            const raw = localStorage.getItem("cleany_consent");
            if (!raw) return;
            const saved = JSON.parse(raw);
            applyConsent({
                statistics: !!saved.statistics,
                marketing: !!saved.marketing,
            });
        } catch (e) {
            // ignore — no consent yet, banner will handle it
        }
    }, []);

    return (
        <BrowserRouter>
            <ScrollToTop />
            <PageviewTracker />
            <div className="App">
                <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/forside-2" element={<ForsideV2Page />} />
                    <Route path="/14-dages-proeve" element={<TrialLandingPage />} />
                    <Route path="/messe-2026" element={<MesseLandingPage />} />
                    <Route path="/demo" element={<DemoPage />} />
                    <Route path="/chat/privacy" element={<ChatPrivacyPage />} />
                    <Route path="/chat/vilkaar" element={<ChatVilkaarPage />} />
                    <Route path="/privatlivspolitik" element={<PrivacyPage />} />
                    <Route path="/handelsbetingelser" element={<TermsPage />} />
                    <Route path="/cookies" element={<CookiesPage />} />
                    <Route path="/databehandleraftale" element={<DpaPage />} />
                    <Route path="/beregn-pris" element={<BeregnPrisPage />} />
                    <Route path="/updates" element={<UpdatesIndexPage />} />
                    <Route path="/updates/:slug" element={<UpdatePostPage />} />
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route path="/crm" element={<CrmLayout />}>
                        <Route index element={<CrmPipeline />} />
                        <Route path="digest" element={<CrmDigest />} />
                        <Route path="leads" element={<CrmLeads />} />
                        <Route path="followups" element={<CrmFollowups />} />
                        <Route path="flow" element={<CrmTemplates />} />
                        <Route path="templates" element={<CrmTemplates />} />
                        <Route path="settings" element={<CrmSettings />} />
                    </Route>
                    <Route path="/admin" element={<AdminLayout />}>
                        <Route index element={<AdminDashboard />} />
                        <Route path="waitlist" element={<AdminTablePage kind="waitlist" />} />
                        <Route path="contacts" element={<AdminTablePage kind="contacts" />} />
                        <Route path="posts" element={<AdminPostsList />} />
                        <Route path="posts/new" element={<AdminPostEditor />} />
                        <Route path="posts/:slug" element={<AdminPostEditor />} />
                        <Route path="legal/dpa" element={<AdminLegalDpaPage />} />
                    </Route>
                    <Route path="*" element={<HomePage />} />
                </Routes>

                <Analytics />
                <WaitlistDialog
                    open={waitlistOpen}
                    onClose={() => setWaitlistOpen(false)}
                    source={waitlistSource}
                />
                <CookieConsent forceOpen={cookieOpen} onClose={() => setCookieOpen(false)} />
                <Toaster richColors position="top-right" />
            </div>
        </BrowserRouter>
    );
}

export default App;
